/**
 * usePushNotifications
 *
 * Manages Capacitor push-notification registration on native iOS/Android.
 * On the web platform this hook is a no-op — all exported values are safe
 * to call unconditionally.
 *
 * Flow:
 *  1. User grants permission (requestPermissions)
 *  2. OS delivers an APNs/FCM token (registration event)
 *  3. Hook calls the `register-device-token` Edge Function to persist it
 *  4. Incoming foreground notifications are surfaced via `lastNotification`
 *  5. Notification-tap actions carry an `actionUrl` for deep-linking
 *
 * Activation: call `register()` once from a top-level component (e.g. App.tsx)
 * after the user has authenticated.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

/** Detects whether the app is running inside a Capacitor native shell. */
function isNative(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof (
      window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }
    ).Capacitor !== "undefined" &&
    (
      window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }
    ).Capacitor?.isNativePlatform?.() === true
  );
}

async function loadPushNotifications() {
  const moduleName = "@capacitor/" + "push-notifications";
  return import(/* @vite-ignore */ moduleName);
}

export interface PushNotificationPayload {
  title: string | undefined;
  body: string | undefined;
  data?: Record<string, string>;
}

type PermissionState = "prompt" | "granted" | "denied" | "unknown";

export function usePushNotifications() {
  const [permissionState, setPermissionState] =
    useState<PermissionState>("unknown");
  const [deviceToken, setDeviceToken] = useState<string | null>(null);
  const [lastNotification, setLastNotification] =
    useState<PushNotificationPayload | null>(null);
  const [registrationError, setRegistrationError] = useState<string | null>(
    null
  );
  const listenersAttached = useRef(false);
  /**
   * Guards against concurrent `register()` calls.  In React 18 strict mode
   * the component may mount-unmount-remount, causing `register()` to be
   * invoked a second time while the first async call is still awaiting
   * `PushNotifications.requestPermissions()`.  By setting this flag
   * synchronously at the top of the function (before any await) we prevent
   * the second call from entering the listener-attachment block.
   */
  const registerInProgress = useRef(false);

  /**
   * Calls the `register-device-token` Edge Function to persist the raw
   * APNs/FCM token in `device_tokens`.
   */
  const persistToken = useCallback(
    async (token: string, platform: "ios" | "android" | "web") => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.access_token) return;

        const response = await supabase.functions.invoke(
          "register-device-token",
          {
            body: {
              token,
              platform,
              provider: platform === "ios" ? "apns" : "fcm",
              appVersion:
                typeof window !== "undefined"
                  ? (
                      window as Window & {
                        Capacitor?: { getAppInfo?: () => { version?: string } };
                      }
                    ).Capacitor?.getAppInfo?.()?.version
                  : undefined,
            },
          }
        );
        if (response.error) {
          logger.warn("register-device-token error:", response.error);
        }
      } catch (err) {
        logger.error("Failed to persist push token:", err);
      }
    },
    []
  );

  /**
   * Requests push permission and registers the device with the OS.
   * Safe to call on web (immediate no-op with `granted` state for UX parity).
   */
  const register = useCallback(async (): Promise<PermissionState> => {
    if (!isNative()) {
      setPermissionState("granted");
      return "granted";
    }

    // Prevent concurrent calls from attaching duplicate listeners.
    // This can happen in React 18 strict-mode (mount → unmount → remount)
    // where `register()` may be invoked a second time before the first
    // async call has set `listenersAttached.current`.
    // We return the current (possibly stale) `permissionState` so callers
    // get a non-blocking answer; the UI will update when the in-progress
    // registration completes and sets state via the "registration" listener.
    if (registerInProgress.current) return permissionState;
    registerInProgress.current = true;

    try {
      const { PushNotifications } = await loadPushNotifications();

      const result = await PushNotifications.requestPermissions();
      const state: PermissionState =
        result.receive === "granted"
          ? "granted"
          : result.receive === "denied"
            ? "denied"
            : "prompt";
      setPermissionState(state);

      if (state !== "granted") return state;

      // Remove duplicate listeners on hot-reloads
      if (!listenersAttached.current) {
        listenersAttached.current = true;

        // Successful registration — OS delivers the device token
        await PushNotifications.addListener("registration", async (token) => {
          setDeviceToken(token.value);
          const rawPlatform =
            typeof (
              window as Window & {
                Capacitor?: { getPlatform?: () => string };
              }
            ).Capacitor?.getPlatform?.() === "string"
              ? (
                  window as Window & {
                    Capacitor?: { getPlatform?: () => string };
                  }
                ).Capacitor!.getPlatform!()
              : "ios";
          const platform: "ios" | "android" | "web" =
            rawPlatform === "android"
              ? "android"
              : rawPlatform === "web"
                ? "web"
                : "ios";
          await persistToken(token.value, platform);
        });

        // Registration failed
        await PushNotifications.addListener(
          "registrationError",
          (err: { error: string }) => {
            setRegistrationError(err.error);
            logger.error("Push registration error:", err);
          }
        );

        // Foreground notification received
        await PushNotifications.addListener(
          "pushNotificationReceived",
          (notification: {
            title: string | undefined;
            body: string | undefined;
            data?: Record<string, string>;
          }) => {
            setLastNotification({
              title: notification.title,
              body: notification.body,
              data: notification.data,
            });
          }
        );

        // Notification tapped — carry actionUrl for deep-link routing
        await PushNotifications.addListener(
          "pushNotificationActionPerformed",
          (action: {
            notification: {
              title?: string;
              body?: string;
              data?: Record<string, string>;
            };
          }) => {
            const n = action.notification;
            setLastNotification({
              title: n.title,
              body: n.body,
              data: n.data,
            });
          }
        );
      }

      await PushNotifications.register();
      registerInProgress.current = false;
      return "granted";
    } catch (err) {
      logger.error("usePushNotifications register failed:", err);
      setRegistrationError(String(err));
      registerInProgress.current = false;
      return "unknown";
    }
  }, [persistToken, permissionState]);

  /**
   * Deactivates push listeners (called on sign-out).
   */
  const unregister = useCallback(async () => {
    if (!isNative() || !listenersAttached.current) return;
    try {
      const { PushNotifications } = await loadPushNotifications();
      await PushNotifications.removeAllListeners();
      listenersAttached.current = false;
    } catch (err) {
      logger.warn("Failed to remove push listeners:", err);
    }
  }, []);

  // Cleanup listeners on unmount
  useEffect(() => {
    return () => {
      if (listenersAttached.current) {
        void unregister();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    permissionState,
    deviceToken,
    lastNotification,
    registrationError,
    register,
    unregister,
    isNative: isNative(),
  };
}
